import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnInit,
  Renderer2,
  ElementRef,
  NgZone,
  OnDestroy,
  Output,
  EventEmitter,
  inject
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

import { inView } from '../helpers';
import { WINDOW } from '../common/tokens';

import { MessageBusService } from './message-bus.service';

@Component({
  selector: 'rlottie',
  template: '',
  host: {
    '[style.display]': '"block"'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RlottieComponent implements OnInit, OnDestroy {
  private readonly bus = inject(MessageBusService);
  private readonly renderer2 = inject(Renderer2);
  private readonly ngZone = inject(NgZone);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input()
  public fileUrl!: string;

  @Input()
  public width!: number;

  @Input()
  public height!: number;

  @Input()
  public loop = true;

  @Input()
  public autoPlay = true;

  @Input()
  set fps(fps: number) {
    this.fpsMs = 1000 / fps;
  }

  @Output()
  public readonly animationLoaded = new EventEmitter<void>();

  @Output()
  public readonly animationEnded = new EventEmitter<void>();

  private canvas!: HTMLCanvasElement;
  private canvasCtx!: CanvasRenderingContext2D;
  private workerId!: number;
  private framesCount!: number;
  private currentFrameId = 0;
  private rafId: number|undefined;
  private isRendering = false;
  private fpsMs = 1000 / 60;
  
  private readonly destroyed$ = new Subject<void>();
  private readonly window = inject(WINDOW);

  get key(): string {
    return `${ this.fileUrl }@${ this.width }`;
  }

  ngOnInit(): void {
    if(!this.bus.isSupported) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.createCanvas();
      this.assignWorker();
      
      inView(this.elementRef.nativeElement)
        .pipe( takeUntil(this.destroyed$) )
        .subscribe((isVisible: boolean) => {
          if(isVisible) {
            if(this.autoPlay) {
              this.isRendering = true;
              this.render();
            }
          } else {
            this.currentFrameId = 0;
            this.canvasCtx.clearRect(0, 0, this.width, this.height);
            this.stopRendering();
          }
        });
    });
  }

  ngOnDestroy(): void {
    this.stopRendering();
    this.destroyed$.next();
  }

  private then: number|undefined;

  render(): void {
    if(!this.isRendering) {
      return;
    }

    if(!this.framesCount) {
      return this.initRenderer();
    }

    if(!this.then) {
      this.then = Date.now();
    }

    const now: number = Date.now();
    const elapsed: number = now - this.then;

    if(elapsed > this.fpsMs) {
      this.then = now - (elapsed % this.fpsMs);

      const frame = this.getFrame(this.currentFrameId);

      if(frame) {
        this.putOnCanvas(frame);
        this.currentFrameId++;

        if(this.currentFrameId > this.framesCount) {
          this.currentFrameId = 0;

          if(!this.loop) {
            this.ngZone.run(() => this.animationEnded.emit());

            return this.stopRendering();
          }
        }

        // request next frame
        this.requestFrame(this.currentFrameId);
      } else {
        this.requestFrame(this.currentFrameId);
      }
    }

    this.rafId = this.window!.requestAnimationFrame(() => this.render());
  }

  private initRenderer(): void {
    const request = {
      key: this.key,
      fileUrl: this.fileUrl,
      size: this.width
    };

    this.bus.initRenderer(this.workerId, request, (framesCount: number) => {
      this.framesCount = framesCount;
      this.ngZone.run(() => this.animationLoaded.emit());
      this.render();
    });
  }

  private getFrame(frameId: number): Uint8ClampedArray|undefined {
    return this.bus.getFrame(this.key, frameId);
  }

  private requestFrame(frameId: number): void {
    this.bus.requestFrame(this.workerId, this.key, frameId);
  }

  private createCanvas(): void {
    this.canvas = this.renderer2.createElement('canvas')!;

    /* this.canvas.style.width = `${ this.width }px`;
    this.canvas.style.height = `${ this.height }px`;*/

    this.canvas.width = this.width;
    this.canvas.height = this.height;

    this.canvasCtx = this.canvas.getContext('2d')!;

    this.renderer2.appendChild(this.elementRef.nativeElement, this.canvas);
  }

  private assignWorker(): void {
    this.workerId = this.bus.assignWorkerId();
  }

  private putOnCanvas(arr: Uint8ClampedArray): void {
    const imageData = new ImageData(arr, this.width, this.height);
    
    this.canvasCtx.putImageData(imageData, 0, 0);
  }

  private stopRendering(): void {
    if(this.rafId) {
      this.window!.cancelAnimationFrame(this.rafId);
    }

    this.rafId = undefined;
    this.isRendering = false;
  }
}